/**
 * Public researcher profile at /researcher/<slug>.
 *
 * Opt-in: only visible when the user toggled it on. Shows level + XP
 * + accuracy + recent solves + top eras/regions. The route guard
 * (api/researcher/:slug) returns 404 for users who haven't opted in.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'

export default function ResearcherProfilePage() {
  const { slug } = useParams()
  const [profile, setProfile] = useState(null)
  const [err, setErr] = useState(null)

  usePageTitle(profile?.name || 'Researcher')

  useEffect(() => {
    api.get(`/researcher/${slug}`)
      .then(setProfile)
      .catch(e => setErr(e.message || 'Could not load profile'))
  }, [slug])

  if (err) return (
    <div className="page" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
      <h1 style={{ fontSize: '1.2rem' }}>Profile not available</h1>
      <p>{err}</p>
    </div>
  )
  if (!profile) return <div className="page" style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading…</div>

  const memberSince = new Date(profile.member_since).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <div className="page researcher-profile">
      <ResearcherProfileBlock profile={profile} memberSince={memberSince} variant="public" />
    </div>
  )
}

// Reusable block (also rendered inside the user's own ProfilePage).
export function ResearcherProfileBlock({ profile, memberSince, variant = 'own' }) {
  return (
    <>
      <header className="researcher-profile__header">
        <div className="researcher-profile__identity">
          <div className="researcher-profile__avatar" aria-hidden>
            {(profile.name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="researcher-profile__eyebrow">{variant === 'own' ? 'You' : 'Researcher'}</div>
            <h1 className="researcher-profile__name">{profile.name || 'Researcher'}</h1>
            {memberSince && (
              <div className="researcher-profile__meta">Member since {memberSince}</div>
            )}
          </div>
        </div>
        <div className={`researcher-profile__level-pill researcher-profile__level-pill--${profile.level?.key}`}>
          {profile.level?.name || 'Novice'}
        </div>
      </header>

      <section className="researcher-profile__progress">
        <div className="researcher-profile__progress-head">
          <span>{profile.xp} XP</span>
          {profile.next_level
            ? <span style={{ color: 'var(--text-muted)' }}>{profile.next_level.minXp - profile.xp} XP to {profile.next_level.name}</span>
            : <span style={{ color: 'var(--text-muted)' }}>Top level reached</span>}
        </div>
        <div className="researcher-profile__progress-bar">
          <div
            className="researcher-profile__progress-fill"
            style={{ width: `${Math.round((profile.level_progress_fraction || 0) * 100)}%` }}
          />
        </div>
      </section>

      <section className="researcher-profile__stats">
        <Stat label="Mysteries solved"     value={profile.counts.mysteries_solved} />
        <Stat label="Attempted"            value={profile.counts.mysteries_attempted} />
        <Stat label="All approvals"        value={profile.counts.approved} />
        <Stat label="Accuracy"             value={`${Math.round((profile.accuracy || 0) * 100)}%`} />
      </section>

      {profile.badges?.length > 0 && (
        <section className="researcher-profile__badges">
          <div className="researcher-profile__section-title">Badges</div>
          <div className="researcher-profile__badge-grid">
            {profile.badges.map(b => (
              <span key={b.key} className={`researcher-profile__badge researcher-profile__badge--${b.category}`}>
                <span className="researcher-profile__badge-icon" aria-hidden>{BADGE_ICON[b.category] || '◆'}</span>
                <span className="researcher-profile__badge-name">{b.name}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {(profile.top_eras?.length > 0 || profile.top_regions?.length > 0) && (
        <section className="researcher-profile__specialisms">
          <div className="researcher-profile__section-title">Top specialisms</div>
          <div className="researcher-profile__chips">
            {profile.top_eras?.map(e => (
              <span key={`era-${e.era}`} className="researcher-profile__chip">{e.era} <em>· {e.n}</em></span>
            ))}
            {profile.top_regions?.map(r => (
              <span key={`reg-${r.code}`} className="researcher-profile__chip">{r.code} <em>· {r.n}</em></span>
            ))}
          </div>
        </section>
      )}

      {profile.recent_solves?.length > 0 && (
        <section className="researcher-profile__recent">
          <div className="researcher-profile__section-title">Recent solves</div>
          <ol className="researcher-profile__recent-list">
            {profile.recent_solves.map((s, i) => (
              <li key={i}>
                <span className="researcher-profile__recent-field">{s.field || s.kind || 'change'}</span>
                <span> on </span>
                <strong>{s.person_name || [s.first_name, s.last_name].filter(Boolean).join(' ') || 'a person'}</strong>
                {s.when && <span className="researcher-profile__recent-when"> · {new Date(s.when).toLocaleDateString('en-GB')}</span>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  )
}

const BADGE_ICON = {
  tier:        '👑',
  volume:      '⚐',
  era:         '✦',
  region:      '◉',
  source:      '✎',
  marketplace: '€',
}

function Stat({ label, value }) {
  return (
    <div className="researcher-profile__stat">
      <div className="researcher-profile__stat-value">{value}</div>
      <div className="researcher-profile__stat-label">{label}</div>
    </div>
  )
}
